import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  ClearAllSelectedVehiclesUuid,
  ConfigurationState,
  InventoryManagementAuctionItemStatus,
  InventoryManagementSearchResult,
  InventoryManagementState,
  LaunchDarklyService,
  StatusFilter,
  VehicleToggleState
} from 'remarketing-angular-library';
import { AnalyticsService, SaComponent, UpdateFooterPadding } from 'smartauction-frontend-micro-library';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { groupBy } from 'lodash';

import { MakeHoldModalComponent } from '../action-list-modals/make-hold-modal/make-hold-modal.component';
import { MakeActiveModalComponent } from '../action-list-modals/make-active-modal/make-active-modal.component';
import { MakeRemovedModalComponent } from '../action-list-modals/make-removed-modal/make-removed-modal.component';
import { MarkArrivedModalComponent } from '../action-list-modals/mark-arrived-modal/mark-arrived-modal.component';
import { ActionListErrorModalComponent } from '../action-list-modals/action-list-error-modal/action-list-error-modal.component';
import { ActionListMessageModalComponent } from '../action-list-modals/action-list-message-modal/action-list-message-modal.component';
import { UpdateSelectedModalComponent } from '../action-list-modals/update-selected-modal/update-selected-modal.component';
import { MakeReserveModalComponent } from '../action-list-modals/make-reserve-modal/make-reserve-modal.component';
import { MakeUnreserveModalComponent } from '../action-list-modals/make-unreserve-modal/make-unreserve-modal.component';
import {
  MultiResultContentBase,
  MultiResultModalComponent,
  MultiResultModalTemplateLabels
} from '../action-list-modals/multi-result-modal/multi-result-modal.component';
import { UpdateResultSummary } from '../../interfaces/update-vehicles-action.interface';
import { take, takeUntil } from 'rxjs/operators';
import { MarkArrivedSuccessModalComponent } from '../action-list-modals/mark-arrived-success-modal/mark-arrived-success-modal.component';
import { GroundingAction } from '../../enums/inventory-management.enum';
import { InventoryManagementOfferList } from 'remarketing-angular-library/interfaces/inventory-management.interface';
import { BulkGroundingHelperService } from '../../services/bulk-grounding/bulk-grounding-helper.service';
import { SearchFilterService } from '../../services/search-filter/search-filter.service';
import { MultiResultModalHelperService } from '../../services/multi-result-modal-helper/multi-result-modal-helper.service';

@Component({
  selector: 'app-action-list',
  templateUrl: './action-list.component.html',
  styleUrls: ['./action-list.component.scss'],
  imports: [
    TranslocoDirective,
    MakeHoldModalComponent,
    MakeActiveModalComponent,
    MakeRemovedModalComponent,
    MarkArrivedModalComponent,
    ActionListErrorModalComponent,
    ActionListMessageModalComponent,
    MarkArrivedSuccessModalComponent,
    UpdateSelectedModalComponent,
    MakeReserveModalComponent,
    MakeUnreserveModalComponent,
    ErrorModalComponent,
    MultiResultModalComponent,
    MarkArrivedSuccessModalComponent
  ]
})
export class ActionListComponent extends SaComponent implements OnInit, OnChanges {

  protected readonly selectedVehicles$ = input<InventoryManagementSearchResult[]>();
  protected readonly selectedVehicleStatus$ = input<StatusFilter>();
  protected readonly isSellReportingFeatureEnabled$ = input<boolean>();
  protected readonly isSellUpdateInventoryFeatureEnabled$ = input<boolean>();
  protected readonly areMultipleTenantsSelected$ = input<boolean>();
  protected readonly isSingleSharedListSelected$ = input<boolean>();
  protected readonly tenantUuid$ = input<string>();

  // keeping as a ViewChild annotation for now until CSS can be validated
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;
  @ViewChild('banner') banner: ElementRef;
  @ViewChild('groundingActionModal') groundingActionModal: MultiResultModalComponent;
  @ViewChild('makeReserveModal') makeReserveModal!: TemplateRef<MakeReserveModalComponent>;

  protected onActionSelected = output<string>();

  protected readonly groundingActionEnum = GroundingAction;
  protected multiResultModalContent: MultiResultContentBase;
  protected isGroundingModalLoading = false;
  protected groundingModalLabels = {} as MultiResultModalTemplateLabels;
  protected modalError: string;
  protected modalMessage: Map<string,string[]>;
  protected groundText = '';
  protected ungroundText = '';
  protected isReserved = false;
  protected tenantName = '';
  analyticsObject = {
    reserve: {
      buttonName: 'Reserve',
      modalName: 'Reserve Vehicle Modal'
    },
    changeReserved: {
      buttonName: 'Change Reserved Dealership',
      modalName: 'Reserve Vehicle Modal'
    },
    unreserve: {
      buttonName: 'Unreserve',
      modalName: 'Unreserve Vehicle Modal'
    },
    ground: {
      buttonName: 'Ground',
      modalName: 'Vehicle Updated Modal'
    },
    unground: {
      buttonName: 'Unground',
      modalName: 'Vehicle Updated Modal'
    },
    makeRemoved: {
      buttonName: 'Remove',
      modalName: 'Make Removed Modal'
    },
    makeActive: {
      buttonName: 'Make Active',
      modalName: 'Make Active Modal'
    },
    makeHold: {
      buttonName: 'Hold',
      modalName: 'Make Hold Modal'
    },
    updateSelected: {
      buttonName: 'Update Selected',
      modalName: 'Update Selected Modal'
    },
    markArrived: {
      buttonName: 'Mark as Arrived',
      modalName: 'Mark Arrived Modal'
    },
    markArrivedSuccess: {
      modalName: 'Status Update Pending Modal'
    }
  };

  private modalRef: BsModalRef;
  private modalConfig: any;
  private footerPadding: number;
  private mixedTenantErrorMessage: string;
  private differentTenantFromUserError: string;
  private selectedVehicleText: string;
  private selectedVehicleTextPlural: string;

  private readonly bsModalService: BsModalService;
  private readonly router: Router;
  private readonly store: Store;
  private readonly _analyticsService: AnalyticsService;
  private readonly _launchDarklyService: LaunchDarklyService;
  private readonly translocoService: TranslocoService;
  private readonly multiResultModalHelperService: MultiResultModalHelperService;
  private readonly bulkGroundingHelperService: BulkGroundingHelperService;
  private readonly searchFilterService: SearchFilterService;

  protected readonly showReserved$ = computed(() => {
    return this.selectedVehicles$()?.some(
      vehicle => vehicle.auctionItemStatus === InventoryManagementAuctionItemStatus.ACTIVE && vehicle.vehicleTypeCode === 'ISV'
    );
  });

  protected readonly showUnreserved$ = computed(() => {
    return this.selectedVehicles$()?.some(
      vehicle => vehicle.auctionItemStatus === InventoryManagementAuctionItemStatus.RESERVED && vehicle.vehicleTypeCode === 'ISV'
    );
  });

  protected readonly showGround$ = computed(() => {
    return this.selectedVehicles$()?.some(
      selectedVehicle =>
        this.isGroundingActionEligible(selectedVehicle) && !this.isGroundedDatePresent(selectedVehicle.groundedDate)
    );
  });

  protected readonly showUnground$ = computed(() => {
    return this.selectedVehicles$()?.some(
      selectedVehicle =>
        this.isGroundingActionEligible(selectedVehicle) && this.isGroundedDatePresent(selectedVehicle.groundedDate)
    );
  });

  protected readonly showRemove$ = computed(() => {
    return (
      this.isSellUpdateInventoryFeatureEnabled$() &&
      !this.isvSelected &&
      (this.selectedVehicleStatus$() === StatusFilter.HOLD_VEHICLES ||
        this.selectedVehicleStatus$() === StatusFilter.ACTIVE_VEHICLES)
    );
  });

  protected readonly showHold$ = computed(() => {
    return (
      this.isSellUpdateInventoryFeatureEnabled$() &&
      this.selectedVehicleStatus$() === StatusFilter.ACTIVE_VEHICLES &&
      !this.isvSelected
    );
  });

  protected readonly showMakeActive$ = computed(() => {
    return (
      this.isSellUpdateInventoryFeatureEnabled$() &&
      this.selectedVehicleStatus$() === StatusFilter.HOLD_VEHICLES &&
      !this.isvSelected
    );
  });

  protected readonly showUpdateSelectedButton$ = computed(() => {
    return (
      this.updateSelectedButtonEnabled &&
      (this.selectedVehicles$()?.some(
        auctionItem => auctionItem.auctionItemStatus === InventoryManagementAuctionItemStatus.ACTIVE || auctionItem.auctionItemStatus === InventoryManagementAuctionItemStatus.HOLD
      ) ||
        this.selectedVehicles$()?.some(
          auctionItem =>
            auctionItem.auctionItemStatus === InventoryManagementAuctionItemStatus.SOLD &&
            (auctionItem.holdProceedsTypeCode === 'H' || auctionItem.holdProceedsTypeCode === 'R')
        ))
    );
  });

  protected readonly showMarkAsArrived$ = computed(() => {
    return this.isSellUpdateInventoryFeatureEnabled$() && this.validVehicleStatusForMarkAsArrivedButtonSelected()
      ? this.handleShowMarkAsArrivedButtonRules()
      : false;
  });

  protected readonly showGenerateReports$ = computed(() => {
    return (
      this.isSellReportingFeatureEnabled$() && this.selectedVehicles$()?.some(vehicle => vehicle.auctionItemStatus === InventoryManagementAuctionItemStatus.SOLD)
    );
  });

  constructor(
  ) {
    super();
    this.bsModalService = inject(BsModalService);
    this.router = inject(Router);
    this.store = inject(Store);
    this._analyticsService = inject(AnalyticsService);
    this._launchDarklyService = inject(LaunchDarklyService);
    this.translocoService = inject(TranslocoService);
    this.multiResultModalHelperService = inject(MultiResultModalHelperService);
    this.bulkGroundingHelperService = inject(BulkGroundingHelperService);
    this.searchFilterService = inject(SearchFilterService);
  }

  ngOnInit() {
    this.getTenantName();
    this.getTranslations();
  }

  ngOnChanges() {
    setTimeout(() => {
      this.updateFooterPadding();
    }, 500);
  }

  ngOnDestroy() {
    this.resetFooterPadding();
    super.ngOnDestroy();
  }

  listActionSelected(action: string) {
    this.onActionSelected.emit(action);
  }

  cancel() {
    this.store.dispatch(new ClearAllSelectedVehiclesUuid());

    const currentStatus = this.store.selectSnapshot(InventoryManagementState.currentStatus);
    // perform a fresh search if we had vehicles selected and the status filter was ALL VEHICLES
    if (currentStatus === StatusFilter.ALL_VEHICLES) {
      this.searchFilterService.setFilterStatus(StatusFilter.ALL_VEHICLES);
    }
  }

  get selectedVehicleTextDisplay(): string {
    return this.selectedVehicles$()?.length > 1 ? this.selectedVehicleTextPlural : this.selectedVehicleText;
  }

  get isAdmin() {
    return this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  isGroundingActionEligible(selectedVehicle: InventoryManagementSearchResult): boolean {
    return (
      (selectedVehicle.auctionItemStatus === 'ACTIVE' || selectedVehicle.auctionItemStatus === 'RESERVED') &&
      selectedVehicle.vehicleTypeCode === 'ISV' &&
      !this.isInvoiced(selectedVehicle.offerLists)
    );
  }

  isGroundedDatePresent(vehicleGroundedDate: string): boolean {
    return !!vehicleGroundedDate?.trim();
  }

  isInvoiced(offerLists: InventoryManagementOfferList[]): boolean {
    let activeOfferList = undefined;
    if (offerLists?.length) {
      activeOfferList = offerLists.find(offerList => offerList.status === 'ACTIVE');
    }
    return activeOfferList !== undefined;
  }

  get isvSelected(): boolean {
    return this.selectedVehicles$()?.some(vehicle => vehicle.vehicleTypeCode === 'ISV');
  }

  validVehicleStatusForMarkAsArrivedButtonSelected(): boolean {
    return (
      this.selectedVehicleStatus$() === StatusFilter.ALL_VEHICLES ||
      this.selectedVehicleStatus$() === StatusFilter.ACTIVE_VEHICLES ||
      this.selectedVehicleStatus$() === StatusFilter.HOLD_VEHICLES ||
      this.selectedVehicleStatus$() === StatusFilter.SOLD_VEHICLES
    );
  }

  // For single auctionItem selection - If vehicle is in transit, show button
  // For multi auctionItem selection- If at least one of selected auctionItems is in transit, show button
  handleShowMarkAsArrivedButtonRules(): boolean {
    return (
      this.selectedVehicles$()?.length > 0 && this.selectedVehicles$().some(auctionItem => auctionItem.inTransit === true)
    );
  }

  get canadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get holdButtonEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-actionlist-holdButtonEnabled', false);
  }

  get makeActiveButtonEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-actionlist-activeButtonEnabled', false);
  }

  get removeButtonEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-actionlist-removeButtonEnabled', false);
  }

  get updateSelectedButtonEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-actionlist-updateSelectedButtonEnabled', false);
  }

  get isInServiceVehiclesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get isBulkGroundingEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-bulkGroundingEnabled', false);
  }

  get isChangeReservedDealershipEnabled() {
    return this._launchDarklyService.getFeatureFlag('sell-changeReservedDealerEnabled', false);
  }

  get markAsArrivedButtonEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-actionlist-markAsArrivedButtonEnabled', false);
  }

  generateReport() {
    let error = false;
    if (!this.canadianTenantEnabled) {
      const nonTenantAuctionVehicles = this.selectedVehicles$().filter(
        selectedVehicle => selectedVehicle.auctionItemTenantUuid !== this.tenantUuid$()
      );
      if (this.areMultipleTenantsSelected$()) {
        this.errorModal.message = this.mixedTenantErrorMessage;
        this.errorModal.show();
        error = true;
      } else if (!this.isAdmin && nonTenantAuctionVehicles.length > 0) {
        this.errorModal.message = this.differentTenantFromUserError;
        this.errorModal.show();
        error = true;
      }
    }

    if (!error) {
      const navExtra: NavigationExtras = {
        state: {
          scrollPosition: window.scrollY
        }
      };
      this._analyticsService.fireEventOnButtonClick('Generate Report', '/inventory/report-previews');
      this.router.navigate(['/inventory/report-previews'], navExtra);
    }
  }

  get statusText() {
    // if our vehicles were toggled from the ALL_VEHICLES status, determine the text based on that vehicle's auction item status
    if (this.selectedVehicleStatus$() === StatusFilter.ALL_VEHICLES) {
      const vehiclesSelected = this.store.selectSnapshot(VehicleToggleState.selectedVehicles) as InventoryManagementSearchResult[];
      return vehiclesSelected[0]?.auctionItemStatus?.toString().toLowerCase() || '';
    }
    return this.selectedVehicleStatus$().toString().toLowerCase();
  }

  setIsReserved(isReserved: boolean) {
    this.isReserved = isReserved;
    this.openModal(this.makeReserveModal, isReserved ? this.analyticsObject.changeReserved : this.analyticsObject.reserve);
  }

  openModal(template, analyticsTranslations) {
    if (analyticsTranslations.buttonName) {
      this._analyticsService.fireEventOnButtonClick(analyticsTranslations.buttonName);
    }

    if (this.modalRef) {
      this.modalRef.hide();
    }
    this.modalConfig = {
      class: 'modal--manage-inventory-action',
      backdrop: 'static',
      keyboard: false
    };
    this.modalRef = this.bsModalService.show(template, this.modalConfig);

    this.trackModalView(`${this.tenantName}: ${analyticsTranslations.modalName}`);
  }

  openGroundingModal(template, groundingAction: GroundingAction) {
    this.openModal(template, groundingAction == this.groundingActionEnum.GROUND ? this.analyticsObject.ground : this.analyticsObject.unground);
    this.isGroundingModalLoading = true;
    this.handleGroundingAction(groundingAction);
  }

  onMultiResultModalClose($event: boolean) {
    if ($event) {
      this.onModalClose(true);
    }
  }

  onModalClose(resetSearch = false) {
    this.modalRef.hide();
    if (resetSearch) {
      // wait for the confirmation modal to hide before clearing vehicle data.
      this.modalRef.onHide?.pipe(take(1)).subscribe(() => {
        this.store.dispatch(new ClearAllSelectedVehiclesUuid());
      });
    }
  }

  onModalError($event, template) {
    this.modalError = $event;
    this.modalRef.hide();
    this.modalConfig = {
      class: 'modal--manage-inventory-action',
      backdrop: 'static',
      keyboard: false
    };
    this.modalRef = this.bsModalService.show(template, this.modalConfig);
  }

  onModalMessage($event, template) {
    this.modalMessage = $event;
    this.modalRef.hide();
    this.modalConfig = {
      class: 'modal--manage-inventory-action',
      backdrop: 'static',
      keyboard: false
    };
    this.modalRef = this.bsModalService.show(template, this.modalConfig);
  }

  selectFailedAndCloseModal($event: MultiResultContentBase) {
    if ($event?.updateSummary) {
      const failedAuctionItems = this.getFailedAuctionItemsFromSelected($event.updateSummary);
      this.multiResultModalHelperService.selectAllFailedVehicles(failedAuctionItems);
      this.multiResultModalContent = this.multiResultModalHelperService.buildDefaultLoadingTemplateData();
      this.modalRef.hide();
    }
  }

  resetGroundingModalTemplate($event: boolean) {
    if ($event) {
      this.multiResultModalContent = this.multiResultModalHelperService.buildDefaultLoadingTemplateData();
    }
  }

  getFailedAuctionItemsFromSelected(updateResultSummary: UpdateResultSummary): InventoryManagementSearchResult[] {
    const failedVins = updateResultSummary.failures.map(vehicleUpdateSummary => vehicleUpdateSummary.vin);
    const failedAuctionItems = [];
    this.selectedVehicles$().forEach(selectedVehicle => {
      if (failedVins.includes(selectedVehicle.itemDetail.vin)) {
        failedAuctionItems.push(selectedVehicle);
      }
    });
    return failedAuctionItems;
  }

  updateFooterPadding() {
    if (this.banner && this.selectedVehicles$()?.length > 0) {
      this.footerPadding = this.setFooterPaddingLimit();
      this.store.dispatch(new UpdateFooterPadding(this.footerPadding));
    } else {
      this.resetFooterPadding();
    }
  }

  setFooterPaddingLimit() {
    return this.banner.nativeElement.clientHeight > 300
      ? this.banner.nativeElement.clientHeight * 0.65
      : this.banner.nativeElement.clientHeight * 0.45;
  }

  resetFooterPadding() {
    this.footerPadding = 0;
    this.store.dispatch(new UpdateFooterPadding(this.footerPadding));
  }

  selectedVehicleMessage(): string {
    const groupedVehicles = groupBy(this.selectedVehicles$(), 'auctionItemStatus');
    let formattedMessages = [];
    for (const status in groupedVehicles) {
      const count = groupedVehicles[status]?.length;
      formattedMessages.push(`${count} ${status.toLocaleLowerCase()} vehicle(s)`);
    }
    return 'You have selected: ' + formattedMessages.join(', ');
  }

  handleGroundingAction(groundingAction: GroundingAction) {
    const selectedVehiclesEligibleForGrounding = this.selectedVehicles$().filter(vehicle =>
      this.isGroundingActionEligible(vehicle)
    );
    const vehiclesApplicableToChosenAction = this.bulkGroundingHelperService.filterSelectedVehiclesBasedOnChosenGroundingAction(
      groundingAction,
      selectedVehiclesEligibleForGrounding
    );
    if (this.areMultipleTenantsSelected$()) {
      this.multiResultModalContent = this.bulkGroundingHelperService.buildMultipleTenantsSelectedErrorContent(
        this.selectedVehicles$().length
      );
    } else {
      this.processGroundingAction(groundingAction, vehiclesApplicableToChosenAction);
    }
  }

  private processGroundingAction(
    groundingAction: GroundingAction,
    vehiclesApplicableToChosenAction: InventoryManagementSearchResult[]
  ): void {
    if (vehiclesApplicableToChosenAction?.length) {
      this.bulkGroundingHelperService
        .submitGroundingAction(groundingAction, vehiclesApplicableToChosenAction)
        .pipe(take(1))
        .subscribe({
          next: result => {
            const formattedResponse = this.multiResultModalHelperService.formatMultiResponseModalResponseDto(result);
            this.multiResultModalContent = this.bulkGroundingHelperService.buildBulkGroundingHappyPathContent(
              formattedResponse,
              groundingAction,
              vehiclesApplicableToChosenAction.length
            );
          },
          error: error => {
            console.error('Error while bulk grounding:', error);
            this.isGroundingModalLoading = false;
            this.multiResultModalContent = this.bulkGroundingHelperService.buildBulkGroundingErrorContent(
              vehiclesApplicableToChosenAction.length
            );
          },
          complete: () => {
            this.isGroundingModalLoading = false;
          }
        });
    } else {
      this.multiResultModalContent = this.bulkGroundingHelperService.buildNoApplicableVehiclesErrorContent(
        vehiclesApplicableToChosenAction.length
      );
      this.isGroundingModalLoading = false;
    }
  }

  getTranslations() {
    const keyParams = {
      'sellModule.manageInventory.actionList': {},
      'sellModule.manageInventory.actionListModal.groundingActionModal': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.selectedVehicleText = translate[0].chooseWhatIt;
        this.selectedVehicleTextPlural = translate[0].chooseWhatThem;
        this.mixedTenantErrorMessage = translate[0].salesReportErrorMessageForDifferentTenants;
        this.differentTenantFromUserError = translate[0].salesReportErrorMessageForOtherTenants;
        this.groundingModalLabels.modalName = translate[1].ariaLabelGroundingActionModal;
        this.groundingModalLabels.headline = translate[1].headline;
        this.groundingModalLabels.resultSummaryLabel = translate[1].resultSummaryLabel;
        this.groundingModalLabels.close = translate[1].close;
        this.groundingModalLabels.closeAndSelectFailed = translate[1].closeAndSelectFailed;
        this.groundText = translate[0].ariaLabelGround;
        this.ungroundText = translate[0].ariaLabelUnground;
      });
  }

  getTenantName() {
    this.tenantName = this.store.selectSnapshot(ConfigurationState.tenantName);
  }

  trackModalView(modalName) {
    try {
      this._analyticsService.track('modalView', modalName);
    } catch(e) {
      console.error(e);
    }
  }
}

